import ErrorIcon from "@mui/icons-material/Error";
import CloseIcon from "@mui/icons-material/Close";
import { Box, IconButton, SxProps, Theme, Typography } from "@mui/material";
import { ReactNode, useEffect, useRef } from "react";

export interface FormErrorBannerProps {
  open: boolean;
  onDismiss: () => void;
  title?: ReactNode;
  message?: ReactNode;
  autoFocus?: boolean;
  sx?: SxProps<Theme>;
}

/**
 * Persistent, accessible feedback for form submission failures.
 * Render it immediately below the form or modal header and above the fields.
 */
export const FormErrorBanner = ({
  open,
  onDismiss,
  title = "Save failed",
  message = "Unable to update this record. Please try again.",
  autoFocus = true,
  sx,
}: FormErrorBannerProps) => {
  const bannerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open && autoFocus) {
      bannerRef.current?.focus();
    }
  }, [autoFocus, open]);

  if (!open) {
    return null;
  }

  return (
    <Box
      ref={bannerRef}
      tabIndex={-1}
      role="alert"
      aria-live="assertive"
      sx={[
        {
          px: 2.25,
          py: 1.75,
          width: "100%",
          boxSizing: "border-box",
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          backgroundColor: "#fff4f4",
          border: "1px solid #e5a3a3",
          borderRadius: "8px",
          color: "text.primary",
          outline: "none",
          "&:focus-visible": {
            boxShadow: "0 0 0 3px rgba(13, 87, 161, 0.25)",
          },
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      <Box
        aria-hidden="true"
        sx={{
          color: "#c62828",
          display: "flex",
          alignItems: "center",
          flexShrink: 0,
        }}
      >
        <ErrorIcon />
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography component="div" sx={{ fontWeight: 700, color: "text.primary", lineHeight: 1.35 }}>
          {title}
        </Typography>
        <Typography component="div" variant="body2" sx={{ color: "text.secondary", mt: 0.25 }}>
          {message}
        </Typography>
      </Box>

      <IconButton
        type="button"
        aria-label="Dismiss error"
        onClick={onDismiss}
        size="small"
        sx={{ color: "text.secondary", flexShrink: 0 }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    </Box>
  );
};
